import {FACTIONS} from "../constants/factions";
import {PLAYER_BONUS_CONFIG, PLAYER_BONUS_TYPES} from "./playerBonus";
import {getBotBonusIntensity} from "./botBonusBehavior";

/**
 * Волна 35: оборона городов ботами (перемещение, кампании, угроза, архетипы).
 *
 * Откуда. Реплеи прода с 05.10 (матчи theLupaChose и замечания ПАТРОКЛА):
 * угрожаемый город бота стоит пустым в 2.7 раза чаще, чем у сильного
 * человека, и его берут в 9 случаях из 10. Держатель — слабый одиночка
 * (медиана размера 3), резерва в 1–2 клетках нет. Угрозу бот видит на один ход,
 * «двойной ход» соперника через перехват инициативы не видит. Захват стоит
 * больше удержания своего города, и экономические фракции обороняются не лучше
 * агрессивных: архетипы к 4 раунду сходятся в RUSH и DEFENSIVE.
 *
 * Здесь константы и веса подчастей волны (каждая — за своим гейтом A/B в
 * utils/bots/abTestGates.js, legacy-игрок играет прежним кодом дословно):
 *  G — гарнизоны по доктрине фракции («защитный класс» отрядов), только у ботов
 *      с бонусом «большой» и выше (W35_GARRISON_MIN_INTENSITY): назначение —
 *      utils/bots/evaluation/garrisons.js, поведение — decideMovementStep,
 *      decideManeuverStep, campaigns, найм держателей — decideRecruitmentStep;
 *  D1 — захват без выживания (decideMovementStep; прочие подчасти D удалены — ниже);
 *  H — доктрина в выборе архетипа (evaluateArchetype);
 *  K — дальность и застревание захватных кампаний (campaigns, decideMovementStep).
 */

// ---- Доктрина обороны фракции ----
export const GARRISON_DOCTRINES = {
  // Инвестируют в оборону, не лезут на рожон и не оставляют открытые города пустыми
  ECONOMIC: 'economic',
  // Держат ценные и угрожаемые города
  MIXED: 'mixed',
  // Гарнизон только при угрозе и в самом ценном угрожаемом городе
  AGGRESSIVE: 'aggressive',
};

// Решение владельца (волна 35): экономические — Вентори, Просветлённые, гномы,
// элементали; агрессивные — зверолюды, инсектоиды, орки, крысолюды, демоны;
// остальные (люди, эльфы, лепреконы, гоблины, великаны, суккубы) — смешанные
export const FACTION_GARRISON_DOCTRINES = {
  [FACTIONS.VENTORY]: GARRISON_DOCTRINES.ECONOMIC,
  [FACTIONS.ENLIGHTENED]: GARRISON_DOCTRINES.ECONOMIC,
  [FACTIONS.DWARVES]: GARRISON_DOCTRINES.ECONOMIC,
  [FACTIONS.ELEMENTALS]: GARRISON_DOCTRINES.ECONOMIC,
  [FACTIONS.HUMANS]: GARRISON_DOCTRINES.MIXED,
  [FACTIONS.ELVES]: GARRISON_DOCTRINES.MIXED,
  [FACTIONS.LEPRECHAUNS]: GARRISON_DOCTRINES.MIXED,
  [FACTIONS.BEASTMEN]: GARRISON_DOCTRINES.AGGRESSIVE,
  [FACTIONS.INSECTOIDS]: GARRISON_DOCTRINES.AGGRESSIVE,
  [FACTIONS.ORCS]: GARRISON_DOCTRINES.AGGRESSIVE,
  [FACTIONS.RATMEN]: GARRISON_DOCTRINES.AGGRESSIVE,
  [FACTIONS.DEMONS]: GARRISON_DOCTRINES.AGGRESSIVE,
};

export const DEFAULT_GARRISON_DOCTRINE = GARRISON_DOCTRINES.MIXED;

/** Доктрина фракции без поправки на бонус (архетип, кап гарнизонов) */
export const getFactionGarrisonDoctrine = (factionId, isStorm = false) => (
  isStorm ? GARRISON_DOCTRINES.AGGRESSIVE : FACTION_GARRISON_DOCTRINES[factionId] || DEFAULT_GARRISON_DOCTRINE
);

// Боты с бонусом «большой» (явное золото) и выше держат города на ступень строже.
// Порог считается от ЧИСЕЛ бонуса, как и пакет «сильных городов» волны 33
// (getBotBonusIntensity): при правке баланса в configs/playerBonus он не
// уедет между уровнями
export const W35_GARRISON_STRICT_MIN_INTENSITY = getBotBonusIntensity(
  PLAYER_BONUS_CONFIG[PLAYER_BONUS_TYPES.BIG]
);

// G действует только у ботов с бонусом «большой» (явное золото) и выше — порог по
// ЧИСЛАМ бонуса, как и ступень строже выше (состав уровней держит тест
// utils/bots/evaluation/garrisons.test.js). A/B 2026-10-26: у обычных ботов
// (r2, 16 032 партии) G стоил −2.33 п.п. винрейта и −1.35 очка (захватов у
// игроков −0.16: армия держала города, а не брала чужие), у рубиновых (r3_great,
// 8160 партий) — винрейт −0.19 п.п. (шум), очки +3.37, угрожаемых город-раундов
// без держателя −10.7 п.п.
export const W35_GARRISON_MIN_INTENSITY = getBotBonusIntensity(
  PLAYER_BONUS_CONFIG[PLAYER_BONUS_TYPES.BIG]
);

/** На ступень строже: агрессивная → смешанная → экономическая (строже нет) */
export const getStricterGarrisonDoctrine = (doctrine) => (
  doctrine === GARRISON_DOCTRINES.AGGRESSIVE ? GARRISON_DOCTRINES.MIXED : GARRISON_DOCTRINES.ECONOMIC
);

// ---- G: какие города открыты и какие обязаны иметь гарнизон ----
// Город «открыт», если живой противник (его отряд или квартал) не дальше этой
// гекс-дистанции от кварталов города. Порядок двух перемещений по дорогам
// (скорость 2 + доп. снабжение), та же зона риска 6, что у новых городов
// бонусного бота волны 33. Отчет 31: потеря города при дистанции до чужих
// кварталов ≤4 — 35%, 5–6 — 29%, >9 — 5.5%. Отряды, которые реально встают на
// квартал за два хода, открывают город и дальше (уровень «скоро», cityCaptureThreat)
export const W35_GARRISON_EXPOSURE_DISTANCE = 6;

// Режим открытости города (калибровка G: A/B r1 и контрфактика прода — удержания
// по дистанции неизбирательны, люди берут из них меньше, чем из пустых городов legacy):
//  'distance' — открыт при живом противнике в W35_GARRISON_EXPOSURE_DISTANCE или
//    угрозе «скоро» и выше;
//  'threat' — открыт только при угрозе «скоро» и выше (NEXT_ROUND, THIS_ROUND или
//    враг встает на квартал за два хода); противник рядом без угрозы город не открывает.
// Внутренний город и последний раунд — одинаково в обоих режимах. Харнесс и стенды
// переопределяют режим через globalThis.__w35GarrisonExposureMode (в проде не задан).
// По умолчанию — 'threat' (калибровка 2026-10-26). Контрфактика прода (cf_calib, 385
// матчей): 'threat' держит города, взятые людьми, так же (r0 27.2% / r1 55.4% против
// 27.3% / 55.6% — 98.7% полезных удержаний режима 'distance'), а невзятые без угрозы —
// 11.0% против 23.4% (legacy 8.4%): снято 83% лишних удержаний; новых удержаний,
// взятых людьми, 4.0% против 3.1% (база среди пустых городов — 5.6%)
export const W35_GARRISON_EXPOSURE_MODES = {
  DISTANCE: 'distance',
  THREAT: 'threat',
};
export const W35_GARRISON_EXPOSURE_MODE = W35_GARRISON_EXPOSURE_MODES.THREAT;

// Режим удержания гарнизона (G v2, по A/B r1: жесткий гарнизон −5.6 п.п. винрейта
// и −0.41 захвата при выигрыше лишь −0.07 потерь — армия теряла атакующие отряды):
//  'hard' — фильтр клеток: гарнизон ходит и маневрирует только по кварталам своего
//    города (прежнее поведение, надбавка W35_GARRISON_HOLD_BONUS);
//  'soft' — фильтра нет: гарнизон получает на квартале своего города ценность
//    держателя (calcW35GarrisonHoldValue — как бонус единственного держателя волны
//    32, не сверх него) и уходит, когда ход дает больше: захват чужого города
//    решает обычная оценка. Из кампаний, форсированных маршей, тяги к фронту и
//    кузнице и из множителя давления гарнизон исключен в обоих режимах.
// Контрфактика прода (cf_v2, 385 матчей, 'threat', пол 0): 'soft' против 'hard' —
// захватов 2625 против 2169 (legacy 3293: возвращено 40% утраченных), у людей 444
// против 364 (legacy 600); удержание городов, взятых людьми, r0 24.8% / r1 53.0%
// против 27.1% / 55.3% (legacy 22.7% / 38.3%), невзятых — 36.3% против 38.5%.
// Харнесс и стенды переопределяют режим через globalThis.__w35GarrisonLockMode
// (SIM_W35_GARRISON_LOCK=hard|soft; в проде не задан)
export const W35_GARRISON_LOCK_MODES = {
  HARD: 'hard',
  SOFT: 'soft',
};
export const W35_GARRISON_LOCK_MODE = W35_GARRISON_LOCK_MODES.SOFT;
// Отдельной охраны самого ценного города экономической доктрины при враге в двух
// ходах без угрозы модели в режиме 'threat' нет: по реплеям прода (385 матчей с
// 05.10) люди не взяли ни одного такого города в том же раунде (0 из 354 взятых
// у экономических фракций), в следующем — 2 из 368, а охрана держала бы 126
// невзятых город-этапов (+41 п.п.) и отказывалась от захватов

// Смешанная доктрина держит открытые города от этих очков (и любой город под
// угрозой следующего раунда, и самый ценный открытый город)
export const W35_GARRISON_MIXED_MIN_CITY_WIN_POINTS = 6;

// Агрессивная доктрина отдает в гарнизоны не больше этой доли отрядов
export const W35_GARRISON_AGGRESSIVE_MAX_UNIT_SHARE = 0.5;

// Надбавка гарнизону за квартал своего города в режиме 'hard'. В валюте захвата
// (CAPTURE_ENEMY_QUARTER = 100): «оборона не дешевле захвата». Уходить с
// кварталов гарнизону и так запрещено фильтром клеток, надбавка нужна, чтобы
// назначенный отряд дошел до свободного квартала, а не остался у казармы
// или на градиенте. В режиме 'soft' вместо нее — ценность держателя города
export const W35_GARRISON_HOLD_BONUS = 100;

// Обреченный держатель: вероятность гибели в собственном ближнем бою (бой в фазе
// владельца обязателен; урон — размер плюс две честные кости урона) от этой доли.
// Такой отряд не назначается держателем при враге на кварталах или рядом с
// ними, а гарнизон, обреченный и на месте, и на каждом доступном квартале, на
// этом тике ходит без фильтра клеток: мертвый держатель город не удержит, а
// противник получит очки за убийство. Частичный шанс выжить (d6(1) против 4 —
// 22%) держание города еще оправдывает. 0.9 — разница размеров от 4: 92% и выше
export const W35_GARRISON_DOOMED_DEATH_CHANCE = 0.9;

// Голод гарнизона: столько размера отряд теряет в фазе снабжения вне досягаемости
// ферм (как W32_HOLDER_STARVATION_LOSS в decideMovementStep). Отряд не больше
// этого размера не назначается держателем города, ни один квартал которого не
// снабжается, — он погибнет в следующей фазе снабжения, а город останется пустым
export const W35_GARRISON_STARVATION_LOSS = 2;

// Маневр гарнизона после боя: шаг с соседней клетки на свободный квартал своего
// города (DEFEND_OWN_CITY); MANEUVER_RETREAT = 10 его не перебьет
export const W35_GARRISON_MANEUVER_BONUS = 30;

// ---- D: оборона дороже захвата ----
// Осталась подчасть D1 (гейт isPreW35DefenseSuicidePlayer): захват без выживания —
// бонус захвата снимается, если отряд по матожиданию погибнет в собственном бою,
// штраф самоубийства и на чужих кварталах (decideMovementStep); своих констант нет.
// D2 (держатель при NEXT_ROUND — ценность города целиком и пол в долях захвата),
// D3 (размен своего города на чужой) и D4 (угроза «скоро» в перемещении) удалены
// по A/B 2026-10-26 — см. utils/bots/abTestGates.js

// ---- H: доктрина в выборе архетипа ----
// Экономическая доктрина уходит в штурм (AGGRESSIVE_RUSH) по слабому городу
// соседа только с локальным перевесом: сумма своих отрядов в этой гекс-дистанции
// от кварталов цели (порядок двух ходов) — не меньше гарнизона цели × RATIO и
// не меньше MIN (минимальный отряд захватной кампании)
export const W35_DOCTRINE_LOCAL_FORCE_DISTANCE = 6;
export const W35_DOCTRINE_RUSH_LOCAL_FORCE_RATIO = 2;
export const W35_DOCTRINE_RUSH_MIN_LOCAL_FORCE = 4;
// «Прикрытый тыл» H (нет обязательного города без держателя) — по открытости
// 'distance', с которой H проверен в A/B r1 (+1.05 п.п. винрейта): переход G на
// 'threat' (калибровка 2026-10-26) снимал лишние гарнизоны, а не запрет штурма с
// открытым тылом. Калибровочный глобал режима G (__w35GarrisonExposureMode) H не меняет
export const W35_DOCTRINE_REAR_EXPOSURE_MODE = W35_GARRISON_EXPOSURE_MODES.DISTANCE;

// ---- K: дальность и застревание захватных кампаний ----
// Захватная кампания — к цели не дальше этой BFS-дистанции (около двух раундов
// пути по дорогам). Случай Вергилия (22.10, R7): марш на 14–16 клеток, пока
// армия Катона и его пустой город стояли в 2–4 клетках
export const W35_CAPTURE_CAMPAIGN_MAX_DISTANCE = 8;
// Большой перевес: своя армия не меньше сильнейшей армии соперника × это —
// дальность кампаний не ограничена
export const W35_CAPTURE_CAMPAIGN_FAR_SUPERIORITY = 2;
// «Ближайший агрессивный сосед»: соперник с наибольшим суммарным размером
// отрядов в этой гекс-дистанции от своих кварталов. Его города — первые среди
// захватных целей, его отряды у своих городов — цели общего градиента
export const W35_AGGRESSIVE_NEIGHBOR_DISTANCE = 6;
// Форсированный марш зверолюдов и крысолюдов: предпочтительный владелец
// (неагрессивная фракция / не раш-доктрина) берется, только если его город не
// дальше этого запаса от ближайшего города любого соперника (или в пределах
// дальности кампании)
export const W35_FORCED_MARCH_PREFERRED_EXTRA_DISTANCE = 2;
// Кампания отряда, стоящего на ближайшей к цели клетке конверта снабжения, когда
// до цели за конвертом дальше, чем разрешает марш L3, снимается через столько
// раундов без прогресса (общий контроль L4 — через 3). Случай Сципиона
// (15.10): d12 шесть раундов (R4–R9) простоял на клетке 8,-8
export const W35_SUPPLY_BLOCKED_STALL_ROUNDS = 2;
